"use client";

import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import PDFViewerModal from "./PDFViewerModal";

interface MarkdownRendererProps {
  children: string;
  className?: string;
  inline?: boolean;
}

/**
 * MarkdownRenderer component that supports HTML in markdown
 *
 * This component uses react-markdown with rehype-raw plugin to render markdown
 * content that includes HTML elements. It allows for mixing markdown syntax with
 * HTML tags in the same content.
 *
 * Example usage:
 * ```jsx
 * <MarkdownRenderer>
 *   {`<div class="note">
 *
 *   Some *emphasis* and <strong>strong</strong>!
 *
 *   </div>`}
 * </MarkdownRenderer>
 * ```
 */
const removeDocumentCitations = (content: string): string => {
  if (!content) return content;
  
  // Remove [Document X, Document Y] patterns
  const citationRegex = /\[Document\s+(?:\d+(?:\s*,\s*Document\s+\d+)*)\]/g;
  return content.replace(citationRegex, '').trim();
}
// Custom Citation component with tooltip and source URL navigation
const Citation: React.FC<{ href: string; children: React.ReactNode; title?: string; sourceUrl?: string; onOpenPDF?: (url: string, title: string) => void }> = ({ href, children, title, sourceUrl, onOpenPDF }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <span className="relative cit">
      <a 
        href={href} 
        className="inline-block font-bold text-blue-600 hover:text-blue-800 cursor-pointer no-underline transition-colors"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={(e) => {
          e.preventDefault();
          console.log('Citation clicked:', { href, title, sourceUrl });
          
          // If source URL is available, handle it based on type
          if (sourceUrl) {
            console.log('Source URL found:', sourceUrl);
            
            // Check if it's our API document endpoint
            if (sourceUrl.startsWith('/api/documents/')) {
              console.log('Opening API document in modal');
              // For PDF documents from our API, open in modal
              const fullUrl = `${process.env.REACT_APP_API_BASE_URL}${sourceUrl.replace("/api", "")}`;
              console.log('Full URL:', fullUrl);
              
              if (onOpenPDF) {
                onOpenPDF(fullUrl, title || 'Document');
              } else {
                console.error('onOpenPDF function not provided');
                // Fallback: open in new tab
                window.open(fullUrl, '_blank', 'noopener,noreferrer');
              }
            } else {
              console.log('Opening external URL in new tab');
              // For external URLs, open in new tab
              window.open(sourceUrl, '_blank', 'noopener,noreferrer');
            }
            return;
          }
          
          console.log('No source URL, scrolling to references section');
          // Fallback: scroll to document in references section
          const element = document.getElementById(href.slice(1));
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            // Highlight the referenced document briefly
            element.classList.add('bg-yellow-100');
            setTimeout(() => element.classList.remove('bg-yellow-100'), 2000);
          } else {
            console.log('No element found with ID:', href.slice(1));
          }
        }}
      >
        [{children}]
      </a>
      
      {/* Custom Tooltip with fade effect */}
      {title && (
        <span className={`absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg z-50 transition-all duration-300 ease-in-out pointer-events-none block ${
          isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1'
        }`}>
          <span className="font-medium whitespace-nowrap block">{removeDocumentCitations(title)}</span>
          {sourceUrl && (
            <span className="text-xs opacity-75 mt-1 block">Click to view source</span>
          )}
          {/* Tooltip arrow */}
          <span className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></span>
        </span>
      )}
    </span>
  );
};

const MarkdownRenderer: React.FC<MarkdownRendererProps> = React.memo(({
  children,
  className,
  inline = false,
}) => {
  // State for PDF modal
  const [pdfModal, setPdfModal] = useState<{
    isOpen: boolean;
    url: string;
    title: string;
  }>({ isOpen: false, url: '', title: '' });

  const openPDFModal = (url: string, title: string) => {
    setPdfModal({ isOpen: true, url, title });
  };

  const closePDFModal = () => {
    setPdfModal({ isOpen: false, url: '', title: '' });
  };
  // Memoize the configuration object to prevent recreation on every render
  const markdownConfig = React.useMemo(() => ({
    // Enable HTML parsing in markdown and GitHub Flavored Markdown features
    rehypePlugins: [rehypeRaw],
    remarkPlugins: [remarkGfm],

    // Define custom components for rendering
    // @ts-ignore - TypeScript definitions might be outdated
    components: {
      // Ensure HTML elements are properly rendered
      p: ({ children }: { children: React.ReactNode }) => 
        inline ? <span>{children}</span> : <p>{children}</p>,
      strong: ({ children }: { children: React.ReactNode }) => (
        <strong>{children}</strong>
      ),
      em: ({ children }: { children: React.ReactNode }) => <em>{children}</em>,
      // Handle links - citations vs external links
      a: ({ href, children, title }: { href?: string; children: React.ReactNode; title?: string }) => {
        // Check if it's a citation link (starts with #doc-)
        if (href && href.startsWith('#doc-')) {
          // Parse title to extract document name and source URL
          let docTitle = title;
          let sourceUrl = undefined;
          
          if (title && title.includes('|')) {
            const [docName, url] = title.split('|');
            docTitle = docName;
            sourceUrl = url;
          }
          
          return <Citation href={href} title={docTitle} sourceUrl={sourceUrl} onOpenPDF={openPDFModal}>{children}</Citation>;
        }
        // External links
        return (
          <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">
            {children}
          </a>
        );
      },
      // Table components for proper rendering
      table: ({ children }: { children: React.ReactNode }) => (
        <table className="markdown-table">{children}</table>
      ),
      thead: ({ children }: { children: React.ReactNode }) => (
        <thead>{children}</thead>
      ),
      tbody: ({ children }: { children: React.ReactNode }) => (
        <tbody>{children}</tbody>
      ),
      tr: ({ children }: { children: React.ReactNode }) => <tr>{children}</tr>,
      th: ({ children }: { children: React.ReactNode }) => <th>{children}</th>,
      td: ({ children }: { children: React.ReactNode }) => <td>{children}</td>,
      // Add more component overrides as needed
    },
  }), [inline]);

  const WrapperComponent = inline ? 'span' : 'div';
  
  return (
    <>
      <WrapperComponent className={`markdown-content ${className || ''}`}>
        {/* @ts-ignore - TypeScript definitions for react-markdown might be outdated */}
        <ReactMarkdown {...markdownConfig}>{children}</ReactMarkdown>
      </WrapperComponent>
      
      {/* PDF Viewer Modal */}
      <PDFViewerModal
        isOpen={pdfModal.isOpen}
        onClose={closePDFModal}
        pdfUrl={pdfModal.url}
        documentTitle={pdfModal.title}
      />
    </>
  );
});

export default MarkdownRenderer;
